import apiFetch from '@/utils/apiFetch';
import { defineStore } from 'pinia';
import DefaultConfig from '@/config/default';
import type { User } from '@/config/types/responses';
import type { Pagination } from '@/config/types/pagination';

// eslint-disable-next-line import/prefer-default-export
export const useUserStore = defineStore('users', {
  state: () => ({
    loading: false,
    error: null as string | null,
    users: [] as User[],
    pagination: {} as Pagination,
    success: null as string | null,
  }),

  actions: {
    async fetchUsers(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');

      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/public/users?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.users = data.data.results ?? [];
        this.pagination = data.data.pagination ?? {};
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async fetchAdminUsers(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/admin/users?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.users = data.data.results ?? [];
        this.pagination = data.data.pagination ?? {};
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async registerUser(firstName: string, lastName: string, emailAddress: string, organizationId?: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const payload: Record<string, string> = { firstName, lastName, emailAddress };
        if (organizationId) payload.organizationId = organizationId;

        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/public/org/users/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(`Error registering user error: ${data.error.error}`);
        }
        this.success = 'Success';
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async fetchUsersByOrganization(organizationId: string, page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/admin/users/list/${organizationId}?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error fetching organization users');
        }
        const data = await res.json();
        this.users = data.data.results ?? [];
        this.pagination = data.data.pagination ?? {};
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async registerAdmin(firstName: string, lastName: string, emailAddress: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/admin/users/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ firstName, lastName, emailAddress }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(`Error registering user error: ${data.error.error}`);
        }
        this.success = 'Success';
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async resetPassword(userId: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/public/users/password/reset`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error?.error || 'Error resetting password');
        }
        this.success = 'Password reset email sent';
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async updateUser(id: string, firstName: string, lastName: string, emailAddress: string, status: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/public/users`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            firstName, lastName, emailAddress, status, id
          }),
        });
        if (!res.ok) {
          throw new Error('Error updating user');
        }
        this.success = 'User updated successfully';
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },
  },
});
